// The `maintenance` family's TENANCY half (epic #4391, #4435): the account /
// installation / repository lifecycle that provisions and gates a tenant, the
// plan-completion and PR-linking rows, the retention/prune crons, and the
// onboarding bot's checked/pr_opened pairs. See `./maintenance.ts`'s header for
// why this is a second file and how the two merge into ONE export.
//
// Several kinds here are ALWAYS `visibleToMaintainer: false` at every writer
// this build has (`janitor.*`, `gardener.sweep_*`, `mcp_provision.checked`,
// `dz_config_migrate.checked`) — either the payload is null on every real row,
// or the kind is a per-repo idempotent CHECK with nothing an operator reads as
// a console line. Both are reasoned `'omit'`, not laziness (#4435's own
// instruction on this point): a fake render exercised only by a fabricated
// test row is coverage theatre.

import type {
  TerminalRenderContext,
  TerminalRendered,
  TerminalRow,
  TerminalSubject,
} from '../../../wire/terminal';
import {
  ForgeMrObservedOutputSchema,
  ForgeMrObserveRefusedOutputSchema,
  ForgePrObservedOutputSchema,
  ForgePrObserveRefusedOutputSchema,
} from '../../audit/payload';
import { renderInstallationPermissionsChanged } from './installation-permissions';
import { num, rec, str } from './maintenance-payload';
import { renderPlanLegDeployed, renderPlanLegReleased } from './plan-legs';
import { renderPlanRecorded } from './plan-recorded';

// ─── mcp_provision.pr_opened (info) ──────────────────────────────────────────
function renderMcpProvisionPrOpened(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  if (repo === null) return null;
  const prNumber = num(row.output, 'prNumber');
  return {
    line: { key: 'terminal.mcp_provision.pr_opened', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo, ...(prNumber !== null ? { prNumber } : {}) },
  };
}

// ─── dz_config_migrate.pr_opened (info) ──────────────────────────────────────
function renderDzConfigMigratePrOpened(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  if (repo === null) return null;
  const prNumber = num(row.output, 'prNumber');
  return {
    line: { key: 'terminal.dz_config_migrate.pr_opened', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo, ...(prNumber !== null ? { prNumber } : {}) },
  };
}

// ─── dz_config_migrate.readied (info) ────────────────────────────────────────
function renderDzConfigMigrateReadied(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  if (repo === null) return null;
  const prNumber = num(row.output, 'prNumber');
  return {
    line: { key: 'terminal.dz_config_migrate.readied', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo, ...(prNumber !== null ? { prNumber } : {}) },
  };
}

// ─── stacked_pr.links_written (info) ─────────────────────────────────────────
function renderStackedPrLinksWritten(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'prNumber');
  if (repo === null || prNumber === null) return null;
  return {
    line: { key: 'terminal.stacked_pr.links_written', params: {} },
    subject: { kind: 'pull_request', id: String(prNumber), label: `${repo}#${prNumber}` },
    refs: { repoFullName: repo, prNumber },
  };
}

// ─── plan_group.merged (ok) ───────────────────────────────────────────────────
function renderPlanGroupMerged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const planId = str(row.output, 'planId');
  if (planId === null) return null;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'prNumber');
  return {
    line: { key: 'terminal.plan_group.merged', params: {} },
    subject: { kind: 'plan', id: planId, label: planId },
    refs: {
      planId,
      ...(repo !== null ? { repoFullName: repo } : {}),
      ...(prNumber !== null ? { prNumber } : {}),
    },
  };
}

// ─── plan.done (ok) ────────────────────────────────────────────────────────────
function renderPlanDone(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const planId = str(row.output, 'planId') ?? str(row.input, 'planId');
  if (planId === null) return null;
  const source = str(row.output, 'source') ?? 'unknown';
  return {
    line: { key: 'terminal.plan.done', params: { source } },
    subject: { kind: 'plan', id: planId, label: planId },
    refs: { planId },
  };
}

// ─── plan.ended (warn) ─────────────────────────────────────────────────────────
function renderPlanEnded(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const planId = str(row.output, 'planId') ?? str(row.input, 'planId');
  if (planId === null) return null;
  return {
    line: { key: 'terminal.plan.ended', params: {} },
    subject: { kind: 'plan', id: planId, label: planId },
    refs: { planId },
  };
}

// ─── plan.reopened (info) ──────────────────────────────────────────────────────
function renderPlanReopened(ctx: TerminalRenderContext): TerminalRendered | null {
  const planId = str(ctx.row.input, 'planId');
  if (planId === null) return null;
  return {
    line: { key: 'terminal.plan.reopened', params: {} },
    subject: { kind: 'plan', id: planId, label: planId },
    refs: { planId },
  };
}

// ─── pr.self_merged (info — NEUTRAL by design, no verdict on legitimacy) ─────
function renderPrSelfMerged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'pr_number');
  if (repo === null || prNumber === null) return null;
  const authorLogin = str(row.input, 'author_login') ?? 'unknown';
  return {
    line: { key: 'terminal.pr.self_merged', params: { author: authorLogin } },
    subject: { kind: 'pull_request', id: String(prNumber), label: `${repo}#${prNumber}` },
    refs: { repoFullName: repo, prNumber },
  };
}

// ─── pr.issue_linked (info) ───────────────────────────────────────────────────
function renderPrIssueLinked(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.output, 'repo_full_name') ?? row.repoFullName;
  const prNumber = num(row.output, 'pr_number');
  const issueNumber = num(row.output, 'issue_number');
  if (repo === null || prNumber === null) return null;
  return {
    line: { key: 'terminal.pr.issue_linked', params: {} },
    subject: { kind: 'pull_request', id: String(prNumber), label: `${repo}#${prNumber}` },
    refs: {
      repoFullName: repo,
      prNumber,
      ...(issueNumber !== null ? { issueNumber } : {}),
    },
  };
}

// ─── pr.split_retired (decision) ─────────────────────────────────────────────
function renderPrSplitRetired(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'prNumber');
  if (repo === null || prNumber === null) return null;
  const action = str(row.output, 'action') ?? 'retired';
  return {
    line: { key: 'terminal.pr.split_retired', params: { action } },
    subject: { kind: 'pull_request', id: String(prNumber), label: `${repo}#${prNumber}` },
    refs: { repoFullName: repo, prNumber },
  };
}

// ─── pr.merge_commit_sha_repaired (info) — always visibleToMaintainer: false ─
function renderPrMergeCommitShaRepaired(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.pr.merge_commit_sha_repaired', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'pull request' },
    refs: {},
  };
}

// ─── pr.merge_timestamps_backfilled (info) — always visibleToMaintainer: false ─
function renderPrMergeTimestampsBackfilled(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.pr.merge_timestamps_backfilled', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'pull request' },
    refs: {},
  };
}

// ─── task.pr_link_refused (warn — a PR's dz-task marker was not honoured) ────
//
// The PULL REQUEST is the subject, not the task: the row is "this PR claimed a
// task", and under `foreign_marker` the task it named need not be this account's.
// The named task rides in `params`, and `reason` is the writer's refusal kind passed
// through as written — a member of `MarkerLinkRefusal['kind']` (`apps/worker`
// `babysit/marker-link.ts`), which is the enumeration; this renderer names none of them,
// so a refusal the worker adds renders without an edit here. The rest of `output`
// (logins, `lane`, `cause`, the task's repo) stays on the audit row.
function renderTaskPrLinkRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const prNumber = num(row.input, 'prNumber');
  if (repo === null || prNumber === null) return null;
  const reason = str(row.output, 'reason') ?? 'refused';
  const taskId = str(row.input, 'taskId');
  return {
    line: {
      key: 'terminal.task.pr_link_refused',
      params: { reason, ...(taskId !== null ? { taskId } : {}) },
    },
    subject: { kind: 'pull_request', id: String(prNumber), label: `${repo}#${prNumber}` },
    refs: { repoFullName: repo, prNumber },
  };
}

// ─── the three retention/prune crons ─────────────────────────────────────────
//
// One `level` per kind (the contract): the success/fail arm's OUTCOME goes in
// params rather than picking a per-row severity, which the type does not admit.
function renderLogRetentionPruned(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const outcome = str(row.output, 'outcome') ?? 'unknown';
  const redacted = num(row.output, 'redacted');
  return {
    line: {
      key: 'terminal.log_retention.pruned',
      params: { outcome, ...(redacted !== null ? { redacted } : {}) },
    },
    subject: { kind: 'session', id: row.sessionId, label: 'log retention prune' },
    refs: {},
  };
}

function renderInboundEventsPruned(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const outcome = str(row.output, 'outcome') ?? 'unknown';
  const deleted = num(row.output, 'deleted');
  return {
    line: {
      key: 'terminal.inbound_events.pruned',
      params: { outcome, ...(deleted !== null ? { deleted } : {}) },
    },
    subject: { kind: 'session', id: row.sessionId, label: 'inbound events prune' },
    refs: {},
  };
}

function renderOutboxPruned(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const outcome = str(row.output, 'outcome') ?? 'unknown';
  const deleted = num(row.output, 'deleted');
  return {
    line: {
      key: 'terminal.outbox.pruned',
      params: { outcome, ...(deleted !== null ? { deleted } : {}) },
    },
    subject: { kind: 'session', id: row.sessionId, label: 'outbox prune' },
    refs: {},
  };
}

// ─── inbound.received (info) — source + event type, NEVER the payload ───────
//
// No real id of `kind: 'account'` exists here — a webhook arrives BEFORE any
// account resolution, let alone a session. Falls back to the session per the
// same contract `installation.linked`/`.revoked` already use for the
// identical gap (#4445's row-id class, never the audit row's own uuid).
function renderInboundReceived(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const sourceKind = str(row.output, 'sourceKind') ?? 'unknown';
  const eventType = str(row.output, 'eventType');
  return {
    line: {
      key: 'terminal.inbound.received',
      params: { sourceKind, ...(eventType !== null ? { eventType } : {}) },
    },
    subject: { kind: 'session', id: row.sessionId, label: `inbound webhook (${sourceKind})` },
    refs: {},
  };
}

// ─── account.provisioned (info) — the ONE writer is always maintainer-visible ─
function renderAccountProvisioned(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const login = str(row.input, 'githubLogin');
  if (login === null) return null;
  const identity = str(row.output, 'identity') ?? 'provisioned';
  return {
    line: { key: 'terminal.account.provisioned', params: { identity } },
    subject: { kind: 'account', id: login, label: login },
    refs: {},
  };
}

// ─── account.login_released / login_renamed / identity_refused ──────────────
//
// Two writers each, disagreeing on visibility (`onboarding-account.ts` marks
// all three `visibleToMaintainer: false`; `identity-sync.ts` does not set the
// flag at all). Read `login` defensively; a withheld row falls back to the
// session subject rather than throwing.
function accountSubject(row: TerminalRow, login: string | null): TerminalSubject {
  if (login !== null) return { kind: 'account', id: login, label: login };
  return { kind: 'session', id: row.sessionId, label: 'account' };
}

function renderAccountLoginReleased(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const login = str(row.input, 'login');
  return {
    line: { key: 'terminal.account.login_released', params: {} },
    subject: accountSubject(row, login),
    refs: {},
  };
}

function renderAccountLoginRenamed(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const login = str(row.input, 'login');
  const fromLogin = str(row.input, 'fromLogin');
  return {
    line: {
      key: 'terminal.account.login_renamed',
      params: { ...(fromLogin !== null ? { from: fromLogin } : {}) },
    },
    subject: accountSubject(row, login),
    refs: {},
  };
}

function renderAccountIdentityRefused(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const login = str(row.input, 'login');
  const reason = str(row.output, 'reason') ?? 'refused';
  return {
    line: { key: 'terminal.account.identity_refused', params: { reason } },
    subject: accountSubject(row, login),
    refs: {},
  };
}

// ─── account.github_id_backfilled (info) — always visibleToMaintainer: false ─
function renderAccountGithubIdBackfilled(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key: 'terminal.account.github_id_backfilled', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: 'account' },
    refs: {},
  };
}

// ─── account.org_idp_seeded (decision — an auth trust anchor changed) ────────
function renderAccountOrgIdpSeeded(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const login = str(row.input, 'githubLogin');
  if (login === null) return null;
  const idpAction = str(row.output, 'idpAction') ?? 'seeded';
  return {
    line: { key: 'terminal.account.org_idp_seeded', params: { action: idpAction } },
    subject: { kind: 'account', id: login, label: login },
    refs: {},
  };
}

// ─── installation.linked / .revoked (info) ───────────────────────────────────
//
// `.linked`'s writer (`apps/worker/src/consumers/onboarding.ts`'s InstallationLinked
// audit) always sets `output.accountId` — the real account this installation was
// just linked to — so the subject is that account, not the installation id.
// `.revoked`'s writer carries no account id at all (`output: { revokedAt }` only),
// so unlike `.linked` there is no real id of `kind: 'account'` to use here: this
// falls back to the session per the contract's stated pattern (`wire/terminal.ts`),
// tracked alongside #4445's row-id class rather than left silently unnamed.
function renderInstallationLinked(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const installationId = str(row.input, 'installationId') ?? numAsId(row.input, 'installationId');
  if (installationId === null) return null;
  const accountId = str(row.output, 'accountId');
  return {
    line: { key: 'terminal.installation.linked', params: {} },
    subject:
      accountId !== null
        ? { kind: 'account', id: accountId, label: accountId }
        : { kind: 'session', id: row.sessionId, label: `installation ${installationId}` },
    refs: {},
  };
}

function renderInstallationRevoked(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const installationId = str(row.input, 'installationId') ?? numAsId(row.input, 'installationId');
  if (installationId === null) return null;
  return {
    line: { key: 'terminal.installation.revoked', params: {} },
    subject: { kind: 'session', id: row.sessionId, label: `installation ${installationId}` },
    refs: {},
  };
}

/** `installationId` rides as a number on some payloads, a string on others. */
function numAsId(v: unknown, key: string): string | null {
  const n = num(v, key);
  return n === null ? null : String(n);
}

// ─── repository.enabled / .disabled / .discovered / .claim_conflict ─────────
function renderRepositoryEnabled(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'fullName') ?? row.repoFullName;
  if (repo === null) return null;
  return {
    line: { key: 'terminal.repository.enabled', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

/**
 * `source` names the writer (#5962). A `transferred_out` row is a repo another account's
 * install took (a GitHub transfer), so it says the repo LEFT. Only some writers carry a
 * `reason`; a row without one gets the bare line, never a `{reason}` placeholder.
 */
function renderRepositoryDisabled(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'fullName') ?? row.repoFullName;
  if (repo === null) return null;
  const reason = str(row.input, 'reason');
  const line: TerminalRendered['line'] =
    str(row.input, 'source') === 'transferred_out'
      ? { key: 'terminal.repository.transferred_out', params: {} }
      : reason === null
        ? { key: 'terminal.repository.disabled_bare', params: {} }
        : { key: 'terminal.repository.disabled', params: { reason } };
  return {
    line,
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

function renderRepositoryDiscovered(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'fullName') ?? row.repoFullName;
  if (repo === null) return null;
  return {
    line: { key: 'terminal.repository.discovered', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

function renderRepositoryClaimConflict(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'fullName') ?? row.repoFullName;
  if (repo === null) return null;
  return {
    line: { key: 'terminal.repository.claim_conflict', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

// ─── repo.ai_first_marked (decision — releases the claim gate's hold) ────────
//
// Two writer shapes: `pr-projection-sink.ts` (`{accountId, repo, prNumber,
// source}`) and `nats-consumers.ts` (`{runId, taskId, accountId, repo,
// claimedRepo, source}`) plus `repos.ts`'s maintainer-declared arm
// (`{repoId, via, ai_first, source}` — no `repo` field at all). Read `repo`
// first; on the maintainer-declared shape fall back to a session subject —
// never a `repo` subject keyed on the writer's internal `repoId`, a different
// id space that would split one repo's rows across two `repo` subjects (same
// rule `repoDonationsRendered` in commerce.ts follows).
function renderRepoAiFirstMarked(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  const source = str(row.input, 'source') ?? 'unknown';
  return {
    line: { key: 'terminal.repo.ai_first_marked', params: { source } },
    subject:
      repo !== null
        ? { kind: 'repo', id: repo, label: repo }
        : { kind: 'session', id: row.sessionId, label: 'repo' },
    refs: repo !== null ? { repoFullName: repo } : {},
  };
}

// ─── repo.visibility_changed (warn — an entitlement fact moved) ──────────────
function renderRepoVisibilityChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'repo') ?? row.repoFullName;
  if (repo === null) return null;
  const nowPrivate = row.output !== null ? (rec(row.output)?.['private'] as unknown) : undefined;
  const visibility = nowPrivate === true ? 'private' : nowPrivate === false ? 'public' : 'changed';
  return {
    line: { key: 'terminal.repo.visibility_changed', params: { visibility } },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

// ─── repo.renamed (info — the repo's name followed GitHub) ───────────────────
function renderRepoRenamed(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.output, 'fullName') ?? row.repoFullName;
  if (repo === null) return null;
  const from = str(row.output, 'previousFullName');
  return {
    line: { key: 'terminal.repo.renamed', params: from !== null ? { from } : {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

// ─── repo.requires_upgrade (warn — enabled repo went private, still gated) ───
//
// `onboarding.ts` writes `fullName`, `repo-visibility.ts` writes `repo` — read
// both.
function renderRepoRequiresUpgrade(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const repo = str(row.input, 'fullName') ?? str(row.input, 'repo') ?? row.repoFullName;
  if (repo === null) return null;
  return {
    line: { key: 'terminal.repo.requires_upgrade', params: {} },
    subject: { kind: 'repo', id: repo, label: repo },
    refs: { repoFullName: repo },
  };
}

// ─── forge.mr.observed (info) — a GitLab MR re-read and graded, read-only ─────
//
// The output is parsed WHOLE against its writer's schema rather than read field by
// field: a line naming a merge verdict without its MR would be worse than no line.
// A withheld or malformed row renders nothing. The subject is a pull request keyed
// `path!iid`, and `refs` stays empty: `repoFullName` links a GitHub repo route, and
// a GitLab project is not one.
/** One `forge.mr.observed` row as a console line; `null` when its output does not parse. */
function renderForgeMrObserved(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgeMrObservedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { path, iid, mergeVerdict, ciVerdict } = parsed.data;
  const label = `${path}!${iid}`;
  return {
    line: {
      key: 'terminal.forge.mr.observed',
      params: { iid, path, merge: mergeVerdict.kind, ci: ciVerdict },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.mr.observe_refused (warn) — the same lane, refused by name ─────────
//
// Parsed whole, like its sibling above. The reason enum is closed, so every reason has
// its own sentence (`terminal.forge.mr.observe_refused.<reason>`) naming the next move —
// a maintainer whose token expired reads "reconnect with a new token", not a code. The
// row carries no project path (a refusal may precede any read), so the subject is the
// MR keyed by GitLab's numeric `projectId!iid`, which the REST paths accept as-is.
/** One `forge.mr.observe_refused` row as its reason's sentence; `null` when unparseable. */
function renderForgeMrObserveRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgeMrObserveRefusedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { projectId, mrIid, reason } = parsed.data;
  const label = `${projectId}!${mrIid}`;
  return {
    line: {
      key: `terminal.forge.mr.observe_refused.${reason}`,
      params: { iid: mrIid, projectId },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.pr.observed (info) — a Gitee PR re-read, read-only (slice 10b) ────
//
// The `forge.mr.observed` posture: parsed whole, a malformed row renders nothing, `refs`
// empty (a Gitee repo is not a GitHub repo route). Subject `owner/repo#number`, Gitee's
// own spelling of a PR. The line names what the read returned: state, Gitee's
// `mergeable`, the rolled-up CI verdict and the reviewer / tester accepts as `n/total`.
/** One `forge.pr.observed` row as a console line; `null` when its output does not parse. */
function renderForgePrObserved(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgePrObservedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { path, number, state, mergeable, ciVerdict, reviewers, testers } = parsed.data;
  const label = `${path}#${number}`;
  return {
    line: {
      key: 'terminal.forge.pr.observed',
      params: {
        number,
        path,
        state,
        mergeable: String(mergeable),
        ci: ciVerdict,
        reviewers: `${reviewers.accepted}/${reviewers.total}`,
        testers: `${testers.accepted}/${testers.total}`,
      },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.pr.observe_refused (warn) — the same lane, refused by name ─────────
//
// One sentence per closed reason (`terminal.forge.pr.observe_refused.<reason>`). A
// refusal may precede any read, so the subject is the PR keyed by Gitee's numeric
// `repoId#number`.
/** One `forge.pr.observe_refused` row as its reason's sentence; `null` when unparseable. */
function renderForgePrObserveRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgePrObserveRefusedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { repoId, number, reason } = parsed.data;
  const label = `${repoId}#${number}`;
  return {
    line: { key: `terminal.forge.pr.observe_refused.${reason}`, params: { number, repoId } },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

/** The tenancy half of the family. `as const` for the same reason as
 * `MAINTENANCE_CORE` — see `./maintenance.ts`. */
export const MAINTENANCE_TENANCY = {
  // Idempotent, frequent per-repo checks — nothing an operator reads as a
  // console line beyond the (rare) write each pairs with below.
  'janitor.run': 'omit',
  'janitor.action': 'omit',
  'janitor.action_unattributed': 'omit',
  'mcp_provision.checked': 'omit',
  'mcp_provision.pr_opened': { level: 'info', actor: 'system', render: renderMcpProvisionPrOpened },
  'dz_config_migrate.checked': 'omit',
  'dz_config_migrate.pr_opened': {
    level: 'info',
    actor: 'system',
    render: renderDzConfigMigratePrOpened,
  },
  'dz_config_migrate.readied': {
    level: 'info',
    actor: 'system',
    render: renderDzConfigMigrateReadied,
  },
  'stacked_pr.links_written': {
    level: 'info',
    actor: 'system',
    render: renderStackedPrLinksWritten,
  },
  'plan_group.merged': { level: 'ok', actor: 'system', render: renderPlanGroupMerged },
  // #3800: rendered like `plan_group.merged` beside it — a plan's DAG landing is a
  // maintainer's console fact (which plan, how many groups), and nothing else shows it.
  'plan.recorded': { level: 'info', actor: 'system', render: renderPlanRecorded },
  'plan.done': { level: 'ok', actor: 'system', render: renderPlanDone },
  'plan.ended': { level: 'warn', actor: 'system', render: renderPlanEnded },
  'plan.reopened': { level: 'info', actor: 'human', render: renderPlanReopened },
  // #5131: the plan-mirror lane's writer. Omitted on purpose — a maintainer sees the GitHub
  // issue itself; the row is the trail. #5132 / #5133 emit it; no producer does here.
  'plan.mirrored': 'omit',
  'plan.leg_deployed': { level: 'ok', actor: 'system', render: renderPlanLegDeployed },
  'plan.leg_released': { level: 'info', actor: 'system', render: renderPlanLegReleased },
  'plan.live_verdict': 'omit', // plan 101 slice 09: trail-only until the epic view grows a chip
  // #5653: the error-issue lane's two writers, same reasoning as
  // `plan.mirrored` beside them — a maintainer sees the filed GitHub issue (or,
  // for `capped`, nothing to see yet), and the rows exist for the trail, not
  // the timeline. `runErrorIssueTick` (the worker's `error.issue` lane) writes both.
  'error_issue.filed': 'omit',
  'error_issue.capped': 'omit',
  // #5654: the reopen pass's two writers, 'omit' for the same reason.
  'error_issue.reopened': 'omit',
  'error_issue.reopen_refused': 'omit',
  // #5657: the error-fix confirmation lane's two writers, 'omit' for the same reason.
  'error_issue.fix_confirmed': 'omit',
  'error_issue.fix_confirm_refused': 'omit',
  'error_issue.settled_closed': 'omit',
  'error_issue.settle_declined': 'omit',
  // #5658: the error-fix release lane's two writers, 'omit' for the same reason.
  'error_issue.fix_released': 'omit',
  'error_issue.fix_release_refused': 'omit',
  // #5659: the error-fix timing lane's two writers, 'omit' likewise.
  'error_issue.fix_timing_noted': 'omit',
  'error_issue.fix_timing_refused': 'omit',
  'pr.self_merged': { level: 'info', actor: 'system', render: renderPrSelfMerged },
  // The WORKER lane detected the link; the human authored the PR, not this
  // row. `'system'` is what the kind implies — `project.ts` overrides to
  // `'human'` on its own when the row carries a `userId`.
  'pr.issue_linked': { level: 'info', actor: 'system', render: renderPrIssueLinked },
  // Closing (or annotating) a PR the platform opened, because triage cut its split.
  'pr.split_retired': { level: 'decision', actor: 'babysitter', render: renderPrSplitRetired },
  'pr.merge_commit_sha_repaired': {
    level: 'info',
    actor: 'system',
    render: renderPrMergeCommitShaRepaired,
  },
  'pr.merge_timestamps_backfilled': {
    level: 'info',
    actor: 'system',
    render: renderPrMergeTimestampsBackfilled,
  },
  'task.pr_link_refused': { level: 'warn', actor: 'system', render: renderTaskPrLinkRefused },
  'log_retention.pruned': { level: 'info', actor: 'system', render: renderLogRetentionPruned },
  'inbound_events.pruned': { level: 'info', actor: 'system', render: renderInboundEventsPruned },
  'outbox.pruned': { level: 'info', actor: 'system', render: renderOutboxPruned },
  'inbound.received': { level: 'info', actor: 'system', render: renderInboundReceived },
  // Both ALWAYS `visibleToMaintainer: false` at their one writer
  // (`cron/gardener.ts`) — payload is null on every real row, so there is
  // nothing to render beyond a tick nobody asked for.
  'gardener.sweep_started': 'omit',
  'gardener.sweep_summary': 'omit',
  'account.provisioned': { level: 'info', actor: 'system', render: renderAccountProvisioned },
  'account.login_released': {
    level: 'info',
    actor: 'system',
    render: renderAccountLoginReleased,
  },
  'account.login_renamed': { level: 'info', actor: 'system', render: renderAccountLoginRenamed },
  // A one-time operator consolidation of one human's duplicate `users` rows
  // (#4483), run from a script against the platform session. Omitted for the same
  // reason the janitor lanes above are: the Terminal is a REPO's timeline, and this
  // event has no repository, no task and nothing a maintainer of one could act on.
  // It is written for the audit trail, which is where it is read.
  'user.rows_consolidated': 'omit',
  'account.identity_refused': {
    level: 'warn',
    actor: 'system',
    render: renderAccountIdentityRefused,
  },
  'account.github_id_backfilled': {
    level: 'info',
    actor: 'system',
    render: renderAccountGithubIdBackfilled,
  },
  'account.org_idp_seeded': {
    level: 'decision',
    actor: 'system',
    render: renderAccountOrgIdpSeeded,
  },
  'installation.linked': { level: 'info', actor: 'system', render: renderInstallationLinked },
  'installation.revoked': { level: 'warn', actor: 'system', render: renderInstallationRevoked },
  // Plan 101 slice 03 door 3 (#7298); the renderer is `./installation-permissions.ts`.
  'installation.permissions_changed': {
    level: 'info',
    actor: 'system',
    render: renderInstallationPermissionsChanged,
  },
  'repository.enabled': { level: 'info', actor: 'system', render: renderRepositoryEnabled },
  'repository.disabled': { level: 'warn', actor: 'system', render: renderRepositoryDisabled },
  'repository.discovered': { level: 'info', actor: 'system', render: renderRepositoryDiscovered },
  'repository.claim_conflict': {
    level: 'warn',
    actor: 'system',
    render: renderRepositoryClaimConflict,
  },
  'repo.ai_first_marked': { level: 'decision', actor: 'system', render: renderRepoAiFirstMarked },
  'repo.visibility_changed': {
    level: 'warn',
    actor: 'system',
    render: renderRepoVisibilityChanged,
  },
  'repo.requires_upgrade': { level: 'warn', actor: 'system', render: renderRepoRequiresUpgrade },
  // #5647: a repo's worker mode was auto-paused after two or more bot pull requests
  // were reverted. The account notification is the surface that tells a
  // maintainer; the Terminal line is a follow-up for an already-stopped repo.
  'agent_mode.auto_paused': 'omit',
  'repo.renamed': { level: 'info', actor: 'system', render: renderRepoRenamed },
  // Platform rows, ALWAYS `visibleToMaintainer: false` at their one writer
  // (`apps/worker` dispatch + `cron.inbound_redrive` + `cron.audit_archive`):
  // they are about a delivery or a storage pass, never a tenant's work, so there
  // is no console line an account would read.
  'repo.rename_refused': 'omit',
  'inbound.terminally_failed': 'omit',
  'inbound.redriven': 'omit',
  'inbound.redelivery_requested': 'omit',
  'inbound.orphaned': 'omit',
  'dispatch.sink_faulted': 'omit',
  'audit.archived': 'omit',
  'audit.archive_pruned': 'omit',
  // Platform housekeeping over spent rows, `visibleToMaintainer: false` at its one
  // writer (`cron/prune-platform-tables.lane.ts`) — nothing a tenant timeline shows.
  'platform_tables.pruned': 'omit',
  'forge.mr.observed': { level: 'info', actor: 'system', render: renderForgeMrObserved },
  'forge.mr.observe_refused': {
    level: 'warn',
    actor: 'system',
    render: renderForgeMrObserveRefused,
  },
  'forge.pr.observed': { level: 'info', actor: 'system', render: renderForgePrObserved },
  'forge.pr.observe_refused': {
    level: 'warn',
    actor: 'system',
    render: renderForgePrObserveRefused,
  },
} as const;
